import { useState, useRef, useEffect } from 'react';
import type { AppNotification } from '../hooks/useAnalysisJobs';

interface Props {
  notifications: AppNotification[];
  unreadCount: number;
  runningCount: number;
  queuedCount: number;
  onOpen: (notif: AppNotification) => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default function NotificationBell({
  notifications,
  unreadCount,
  runningCount,
  queuedCount,
  onOpen,
  onMarkAllRead,
  onDismiss,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const activeCount = runningCount + queuedCount;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 text-slate-500 dark:text-slate-400 shader:text-white/70 transition-colors"
        aria-label="Notifications"
        title="Notifications"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-violet-600 text-white text-[10px] font-bold leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] rounded-xl bg-white dark:bg-slate-800 shader:bg-slate-800 border border-slate-200 dark:border-slate-700 shader:border-white/15 shadow-xl z-[70] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</span>
            {unreadCount > 0 && (
              <button onClick={onMarkAllRead} className="text-xs text-violet-600 dark:text-violet-400 hover:underline">
                Tout marquer comme lu
              </button>
            )}
          </div>

          {activeCount > 0 && (
            <div className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
              <svg className="w-3.5 h-3.5 animate-spin text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" />
              </svg>
              {runningCount > 0 && `${runningCount} analyse${runningCount > 1 ? 's' : ''} en cours`}
              {runningCount > 0 && queuedCount > 0 && ' · '}
              {queuedCount > 0 && `${queuedCount} en attente`}
            </div>
          )}

          <div className="max-h-80 overflow-y-auto scrollbar-evin">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400 dark:text-slate-500">
                Aucune notification pour l'instant.
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`group relative flex items-start gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700/60 cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/40 ${
                    !n.read ? 'bg-violet-50/50 dark:bg-violet-900/10' : ''
                  }`}
                  onClick={() => {
                    onOpen(n);
                    setOpen(false);
                  }}
                >
                  <span
                    className={`mt-1 shrink-0 w-2 h-2 rounded-full ${
                      n.read ? 'bg-transparent' : n.kind === 'analysis_error' ? 'bg-red-500' : 'bg-violet-500'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug line-clamp-2">{n.message}</p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{timeAgo(n.createdAt)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(n.id);
                    }}
                    className="shrink-0 p-1 rounded text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Ignorer"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
